import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserGear, Plus, Pencil, Trash, Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access to all features' },
  { value: 'admin_nofee', label: 'Admin (No Finance)', desc: 'All features except Reports & Revenue' },
  { value: 'attendance_only', label: 'Attendance Only', desc: 'Dashboard + Mark Attendance only' },
  { value: 'face_only', label: 'Face Only', desc: 'Face Recognition mode only' },
];

const getRoleBadge = (role) => {
  const styles = {
    admin: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
    admin_nofee: 'bg-violet-600/20 text-violet-400 border-violet-500/30',
    attendance_only: 'bg-amber-600/20 text-amber-400 border-amber-500/30',
    face_only: 'bg-teal-600/20 text-teal-400 border-teal-500/30',
  };
  const labels = {
    admin: 'Admin',
    admin_nofee: 'Admin (No Finance)',
    attendance_only: 'Attendance Only',
    face_only: 'Face Only',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[role] || styles.admin}`}>
      {labels[role] || role}
    </span>
  );
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'admin', service_id: '' });

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchServices = useCallback(async () => {
    try {
      const res = await api.get('/api/services', { params: { service_id: 'all' } });
      setServices(res.data);
    } catch (err) {
      // ignore
    }
  }, []);

  const serviceName = (id) => services.find((s) => s.id === id)?.name || 'All Services';

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/users');
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchServices(); }, [fetchUsers, fetchServices]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Username and password are required');
      return;
    }
    try {
      await api.post('/api/admin/users', formData);
      toast.success(`User "${formData.username}" created`);
      setIsAddOpen(false);
      setFormData({ username: '', password: '', role: 'admin', service_id: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (formData.username !== selectedUser.username) payload.username = formData.username;
    if (formData.password) payload.password = formData.password;
    if (formData.role !== selectedUser.role) payload.role = formData.role;
    if ((formData.service_id || '') !== (selectedUser.service_id || '')) payload.service_id = formData.service_id || '';

    if (Object.keys(payload).length === 0) {
      toast.info('No changes made');
      setIsEditOpen(false);
      return;
    }

    try {
      await api.put(`/api/admin/users/${selectedUser.username}`, payload);
      toast.success('User updated successfully');
      setIsEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/admin/users/${selectedUser.username}`);
      toast.success(`User "${selectedUser.username}" deleted`);
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({ username: user.username, password: '', role: user.role, service_id: user.service_id || '' });
    setShowPassword(false);
    setIsEditOpen(true);
  };

  const openDelete = (user) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600/20 rounded-xl">
              <UserGear size={28} weight="duotone" className="text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-50">User Management</h1>
              <p className="text-sm text-slate-400">{users.length} registered users</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setFormData({ username: '', password: '', role: 'admin', service_id: '' });
              setShowPassword(false);
              setIsAddOpen(true);
            }}
            data-testid="add-user-btn"
            className="bg-blue-600 hover:bg-blue-500 flex items-center gap-2"
          >
            <Plus size={18} weight="bold" />
            Add User
          </Button>
        </div>

        {/* Users Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading users...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-950 border-b border-slate-800">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Username</th>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Role</th>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Service</th>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Created</th>
                  <th className="text-right px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {users.map((user, idx) => (
                  <tr key={user.username} className="hover:bg-slate-800/50" data-testid={`user-row-${user.username}`}>
                    <td className="px-6 py-4 text-slate-400 font-medium">{idx + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center text-white font-bold text-sm">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-slate-50 font-semibold">{user.username}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4 text-slate-300 text-sm">{serviceName(user.service_id)}</td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(user)}
                          data-testid={`edit-user-${user.username}`}
                          className="p-2 hover:bg-slate-700 rounded-md text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit User"
                        >
                          <Pencil size={18} weight="duotone" />
                        </button>
                        <button
                          onClick={() => openDelete(user)}
                          data-testid={`delete-user-${user.username}`}
                          className="p-2 hover:bg-slate-700 rounded-md text-rose-400 hover:text-rose-300 transition-colors"
                          title="Delete User"
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Role Legend */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={20} weight="duotone" className="text-blue-400" />
            <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Role Permissions</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ROLES.map(r => (
              <div key={r.value} className="flex items-center gap-3 p-3 bg-slate-950 rounded-lg border border-slate-800">
                {getRoleBadge(r.value)}
                <span className="text-slate-400 text-sm">{r.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus size={22} weight="bold" className="text-blue-400" />
              Add New User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Username</label>
              <Input
                data-testid="new-username-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-50"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Password</label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-50 pr-10"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Role</label>
              <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50" data-testid="new-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value} className="text-slate-50">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Service Access</label>
              <Select value={formData.service_id || '__all__'} onValueChange={(val) => setFormData({ ...formData, service_id: val === '__all__' ? '' : val })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50" data-testid="new-service-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="__all__" className="text-slate-50">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-slate-50">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="save-new-user-btn" className="w-full bg-blue-600 hover:bg-blue-500">
              Create User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Pencil size={22} weight="bold" className="text-blue-400" />
              Edit User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Username</label>
              <Input
                data-testid="edit-username-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-50"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">New Password (leave blank to keep current)</label>
              <div className="relative">
                <Input
                  data-testid="edit-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-50 pr-10"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Role</label>
              <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50" data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value} className="text-slate-50">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Service Access</label>
              <Select value={formData.service_id || '__all__'} onValueChange={(val) => setFormData({ ...formData, service_id: val === '__all__' ? '' : val })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50" data-testid="edit-service-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="__all__" className="text-slate-50">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-slate-50">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="save-edit-user-btn" className="w-full bg-blue-600 hover:bg-blue-500">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-rose-400">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 mt-2">
            Are you sure you want to delete <span className="font-bold text-slate-50">{selectedUser?.username}</span>? This action cannot be undone.
          </p>
          <div className="flex gap-3 mt-6">
            <Button onClick={() => setIsDeleteOpen(false)} variant="outline" className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={handleDelete} data-testid="confirm-delete-user-btn" className="flex-1 bg-rose-600 hover:bg-rose-500">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
